/**
 * **미리 정한 읽기** — 질문 주소(`/service?question=…&read=…`)에 실어 길잡이 모델을 거치지 않고 바로 답하는 읽기.
 * 무엇을 눌렀는지 아는 자리(비회원 홈 카드)가 짓고, 서비스 홈 대화 화면이 읽어 `ask.answer` 를 부른다.
 *
 * 🔑 주소로 들어온 값은 믿지 않는다 — 조건표(`readAskReading`)로 거르고, 홈에서 답하는 읽기(`isAskAnswerReading`)만 받는다.
 *   걸러져 빠진 조건이 하나라도 있으면 통째로 버린다(부르는 쪽은 길잡이로 묻는다).
 *   `ask.answer` 엔드포인트는 받은 읽기를 같은 두 판정으로 다시 거른다 — 브라우저의 판정은 길을 고를 뿐이다.
 * ⚠️ `'use client'` 를 붙이지 않는다 — 순수 함수다.
 */

import type { AskFilters, AskReading, AskScreenId } from 'src/types/ask/ask-ops';

import { readAskReading } from './ask-reading';
import { isAskAnswerReading } from './ask-answer';

/** 미리 정한 읽기의 재료 — 화면과 조건만 싣는다(질문 글은 주소의 `question` 이다) */
export type AskPreset = { screen: AskScreenId; filters: AskFilters };

/**
 * 주소의 `read` 값을 짓는다.
 * @returns JSON 글자 — 주소에 넣을 때는 부르는 쪽이 `encodeURIComponent` 한다
 */
export function toAskPresetParam(preset: AskPreset): string {
  return JSON.stringify({ screen: preset.screen, filters: preset.filters });
}

/**
 * 주소의 `read` 값 → 바로 답할 읽기.
 * @param raw 주소의 `read`(JSON 글자) 또는 이미 푼 값
 * @param question 주소의 `question` — 읽기의 질문이 된다
 * @returns 못 읽었거나 · 홈에서 답하지 않는 읽기거나 · 조건 하나라도 걸러졌거나 · 조건이 없으면 `null`
 */
export function readAskPresetReading(raw: unknown, question: string): AskReading | null {
  const text = question.trim();
  if (!text) return null;
  const box = typeof raw === 'string' ? parseJson(raw) : raw;
  if (box == null || typeof box !== 'object' || Array.isArray(box)) return null;
  const { screen, filters } = box as { screen?: unknown; filters?: unknown };
  if (filters == null || typeof filters !== 'object' || Array.isArray(filters)) return null;
  const given = Object.keys(filters as Record<string, unknown>);
  if (given.length === 0) return null;

  const reading = readAskReading({ screen, filters }, text, 'rule');
  if (reading.screen === 'unsupported' || !isAskAnswerReading(reading)) return null;
  if (reading.unmatched.length > 0 || Object.keys(reading.filters).length !== given.length) return null;
  return reading;
}

// ---- 이하 비공개 ----------------------------------------------------------

function parseJson(raw: string): unknown {
  if (!raw.trim()) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
